/**
 * @file Button.tsx
 * @brief Accessible button primitive with shadcn/ui-style variants.
 *
 * @details
 * - Uses `class-variance-authority` for type-safe `variant` and `size`
 *   props.
 * - `asChild` (via Radix `Slot`) lets the same styles be applied to a
 *   `<Link>` or other interactive element when needed.
 * - **Pill shape** (`rounded-full`) deliberately mirrors the mobile
 *   app's CTA aesthetic — see `loginPillButton` / `registerPillButton`
 *   in `FRONTEND/src/screens/Auth/LoginScreen.tsx` (height 68 +
 *   borderRadius 34) and the alert action buttons in
 *   `FRONTEND/src/screens/CompanyAdmin/components/UserEditModal.tsx`
 *   (height 50 + borderRadius 25). Larger horizontal padding
 *   (`px-5` / `px-6`) compensates so the rounded ends don't crowd the
 *   label.
 *
 * @dependencies
 * - class-variance-authority
 * - @radix-ui/react-slot
 * - shared/theme/cn
 *
 * @author DES
 * @date 26-05-2026
 */

import { Slot } from "@radix-ui/react-slot";
import { type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/shared/theme/cn";
import { buttonVariants } from "./buttonVariants";

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";
